import {
    SegmentActivation,
    SegmentActivationStatus,
    segmentActivationStatusToJSON,
    TradingAccountStatus,
    tradingAccountStatusToJSON
} from '../../../proto/generated/trading-bridge'

export const formatSegmentLabel = (exchange: string, segment: string) =>
    `${exchange}-${segment}`

export const formatActivationStatus = (status: SegmentActivationStatus): string => {
    switch (status) {
        case SegmentActivationStatus.SEGMENT_ACTIVATION_PENDING:
            return 'Pending'
        case SegmentActivationStatus.SEGMENT_ACTIVATION_ACTIVE:
            return 'Active'
        case SegmentActivationStatus.SEGMENT_ACTIVATION_DEACTIVATED:
            return 'Deactivated'
        default:
            return segmentActivationStatusToJSON(status)
    }
}

export const getActivationStatusColor = (status: SegmentActivationStatus): 'default' | 'success' | 'warning' | 'error' => {
    switch (status) {
        case SegmentActivationStatus.SEGMENT_ACTIVATION_ACTIVE:
            return 'success'
        case SegmentActivationStatus.SEGMENT_ACTIVATION_PENDING:
            return 'warning'
        case SegmentActivationStatus.SEGMENT_ACTIVATION_DEACTIVATED:
            return 'error'
        default:
            return 'default'
    }
}

export const formatEpochMillis = (millis: number): string => {
    if (!millis) return '—'
    return new Date(millis).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
}

export const formatAccountStatus = (status: TradingAccountStatus): string => {
    switch (status) {
        case TradingAccountStatus.ACTIVE:
            return 'Active'
        case TradingAccountStatus.KYC_INITIATED:
            return 'Pending review'
        case TradingAccountStatus.DOCUMENTS_SUBMITTED:
            return 'Documents submitted'
        case TradingAccountStatus.DOCUMENTS_VERIFIED:
            return 'Documents verified'
        case TradingAccountStatus.KYC_REJECTED:
            return 'KYC rejected'
        default:
            return tradingAccountStatusToJSON(status)
    }
}

export const hasActiveKillSwitch = (segments: SegmentActivation[]): boolean =>
    segments.some((s) => s.killSwitchActive)

export const isPendingDematApplication = (status: TradingAccountStatus): boolean =>
    status === TradingAccountStatus.KYC_INITIATED ||
    status === TradingAccountStatus.DOCUMENTS_SUBMITTED

export const BANK_ACCOUNT_TYPES = ['SAVINGS', 'CURRENT'] as const
export const DEPOSITORIES = ['NSDL', 'CDSL'] as const

/** Indian Financial System Code: 4 letters + 0 + 6 alphanumeric branch code. */
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/

export const IFSC_VALIDATION_MESSAGE =
    'Enter a valid 11-character IFSC code (e.g. HDFC0001234)'

export const normalizeIfsc = (ifsc: string): string =>
    ifsc.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11)

export const isValidIfsc = (ifsc: string): boolean =>
    IFSC_REGEX.test(normalizeIfsc(ifsc))

/** Reveal only the last 4 characters; leaves already-masked values unchanged. */
export const maskSensitiveIdentifier = (value: string): string => {
    const trimmed = value.trim()
    if (!trimmed || trimmed === '—') return trimmed
    if (trimmed.includes('*')) return trimmed
    if (trimmed.length <= 4) return '*'.repeat(trimmed.length)
    return `${'*'.repeat(trimmed.length - 4)}${trimmed.slice(-4)}`
}

export const DEFAULT_SEGMENT_SPECS = [
    { exchange: 'NSE', segment: 'CM' },
    { exchange: 'NSE', segment: 'FO' },
    { exchange: 'NSE', segment: 'CD' },
    { exchange: 'BSE', segment: 'CM' },
    { exchange: 'BSE', segment: 'FO' },
    { exchange: 'BSE', segment: 'CD' }
] as const
